/**
 * Мобильное приложение (PWA): сервис-воркер, офлайн-данные, установка.
 * Подробно — docs/PWA.md.
 *
 * Внутри настольного приложения ничего из этого не включается: там
 * интерфейс грузится с сервера оболочкой и должен обновляться сразу по
 * Ctrl+R, а сервис-воркер держал бы прежнюю сборку.
 */

import { isDesktop } from './desktop.ts';

/** Регистрация воркера. Только в собранной версии: в разработке он мешал бы Vite. */
export function registerServiceWorker(): void {
  if (isDesktop() || !import.meta.env.PROD || !('serviceWorker' in navigator)) return;
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js', { scope: '/' }).catch((err: unknown) => {
      console.warn('[pwa] сервис-воркер не зарегистрирован:', err);
    });
  });
}

/**
 * Стереть сохранённые на телефоне данные (вход, выход). Воркер делает это и
 * сам, увидев запрос входа или выхода, — здесь второй, явный путь.
 */
export function purgeOfflineData(): void {
  navigator.serviceWorker?.controller?.postMessage({ type: 'tw:purge' });
}

// ── Свежесть данных ─────────────────────────────────────────────────────────

const STALE_EVENT = 'tw:stale-data';

/**
 * Отметить ответ API: пришёл из сети или из сохранённого (воркер ставит
 * X-TW-Offline). Плашка «показаны сохранённые данные» слушает это событие.
 */
export function noteResponse(response: Response): void {
  const stale = response.headers.get('x-tw-offline') === '1';
  window.dispatchEvent(new CustomEvent<boolean>(STALE_EVENT, { detail: stale }));
}

export function onStaleData(handler: (stale: boolean) => void): () => void {
  const listener = (e: Event) => handler((e as CustomEvent<boolean>).detail);
  window.addEventListener(STALE_EVENT, listener);
  return () => window.removeEventListener(STALE_EVENT, listener);
}

// ── Установка ───────────────────────────────────────────────────────────────

/** Открыто как установленное приложение (с экрана «Домой»), а не во вкладке. */
export function isStandalone(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

export function isIos(): boolean {
  // iPadOS выдаёт себя за Mac, но с сенсорным экраном.
  return /iphone|ipad|ipod/i.test(navigator.userAgent) || (/macintosh/i.test(navigator.userAgent) && navigator.maxTouchPoints > 1);
}

export function isPhoneLike(): boolean {
  return window.matchMedia('(pointer: coarse)').matches;
}

/** Событие Chrome на Android «можно установить»; на iPhone его нет. */
export interface InstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let deferred: InstallPromptEvent | null = null;
const INSTALL_EVENT = 'tw:install-available';

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    // Свой момент и своя кнопка вместо всплывающей плашки браузера.
    e.preventDefault();
    deferred = e as InstallPromptEvent;
    window.dispatchEvent(new Event(INSTALL_EVENT));
  });
}

export function canPromptInstall(): boolean {
  return deferred !== null;
}

export function onInstallAvailable(handler: () => void): () => void {
  window.addEventListener(INSTALL_EVENT, handler);
  return () => window.removeEventListener(INSTALL_EVENT, handler);
}

/** Показать системный диалог установки (Android). true — установили. */
export async function promptInstall(): Promise<boolean> {
  if (!deferred) return false;
  const e = deferred;
  deferred = null;
  await e.prompt();
  return (await e.userChoice).outcome === 'accepted';
}
